import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Chip
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit', label: 'Credit Card' }
];

const AccountsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewAccounts, canEditAccounts, canDeleteAccounts } = usePermissions();
  
  // State management
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  
  // Category selection dialog states
  const [categorySelectionDialogOpen, setCategorySelectionDialogOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [dialogSelectedCategory, setDialogSelectedCategory] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    account_name: '',
    last_four_digits: '',
    bank_name: '',
    account_type: '',
    category_id: ''
  });

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });
      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      // Fetch decrypted accounts
      const { data: accountsData, error: accountsError } = await supabase
        .rpc('get_expense_accounts_decrypted', { company_id_param: companyId });
      if (accountsError) throw accountsError;

      // Fetch categories to map category names
      const { data: categoriesData, error: categoriesError } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });
      if (categoriesError) throw categoriesError;

      // Map category data to accounts
      const accountsWithCategories = (accountsData || []).map(account => ({
        ...account,
        category: account.category_id 
          ? categoriesData.find(cat => cat.id === account.category_id)
          : null
      }));

      // Sort by account name
      accountsWithCategories.sort((a, b) => 
        (a.account_name || '').localeCompare(b.account_name || '')
      );

      setAccounts(accountsWithCategories);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load expense accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchCategories();
    fetchAccounts();
  }, [companyId]);

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditMode(true);
      setFormData({
        id: account.id,
        account_name: account.account_name || '',
        last_four_digits: account.last_four_digits || '',
        bank_name: account.bank_name || '',
        account_type: account.account_type || '',
        category_id: account.category_id || ''
      });
    } else {
      setEditMode(false);
      setFormData({
        id: '',
        account_name: '',
        last_four_digits: '',
        bank_name: '',
        account_type: '',
        category_id: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData({
      id: '',
      account_name: '',
      last_four_digits: '',
      bank_name: '',
      account_type: '',
      category_id: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.account_name.trim()) {
      setError('Account name is required.');
      return;
    }
    
    if (!formData.last_four_digits.trim() || formData.last_four_digits.length !== 4) {
      setError('Last 4 digits must be exactly 4 characters.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // Encrypt sensitive fields
      const { data: encryptedAccountName, error: nameError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.account_name.trim() });
      if (nameError) throw nameError;

      const { data: encryptedLastFour, error: lastFourError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.last_four_digits.trim() });
      if (lastFourError) throw lastFourError;

      const { data: encryptedBankName, error: bankError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.bank_name.trim() || null });
      if (bankError) throw bankError;

      const { data: encryptedAccountType, error: typeError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.account_type || null });
      if (typeError) throw typeError;

      const accountData = {
        company_id: companyId,
        account_name_encrypted: encryptedAccountName,
        last_four_digits_encrypted: encryptedLastFour,
        bank_name_encrypted: encryptedBankName,
        account_type_encrypted: encryptedAccountType,
        category_id: formData.category_id || null
      };

      if (editMode) {
        const { error } = await supabase
          .from('expense_accounts')
          .update(accountData)
          .eq('id', formData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('expense_accounts')
          .insert(accountData);
        if (error) throw error;
      }

      await fetchAccounts();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Account ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving account:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} account.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;
    
    try {
      const { error } = await supabase
        .from('expense_accounts')
        .delete()
        .eq('id', accountToDelete.id);
      if (error) throw error;
      
      await fetchAccounts();
      setSnackbar({ open: true, message: 'Account deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting account: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setAccountToDelete(null);
  };

  const getAccountTypeLabel = (type) => {
    const accountType = ACCOUNT_TYPES.find(t => t.value === type);
    return accountType ? accountType.label : type;
  };

  // Category selection dialog handlers
  const handleOpenCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(true);
    setCategorySearchTerm('');
    setDialogSelectedCategory(null);
  };

  const handleCloseCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(false);
    setCategorySearchTerm('');
    setDialogSelectedCategory(null);
  };

  const handleDialogCategorySelect = (category) => {
    setDialogSelectedCategory(category);
  };

  const handleConfirmCategorySelection = () => {
    if (dialogSelectedCategory) {
      setFormData(prev => ({ ...prev, category_id: dialogSelectedCategory.id }));
      setCategorySelectionDialogOpen(false);
      setCategorySearchTerm('');
      setDialogSelectedCategory(null);
    }
  };

  const getFilteredCategoriesForSelection = () => {
    if (!categorySearchTerm) return categories || [];
    
    const searchLower = categorySearchTerm.toLowerCase();
    return (categories || []).filter(category => 
      category.name.toLowerCase().includes(searchLower)
    );
  };

  // Filter accounts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAccounts(accounts);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = accounts.filter(acc => 
      acc.account_name?.toLowerCase().includes(searchLower) ||
      acc.bank_name?.toLowerCase().includes(searchLower) ||
      acc.last_four_digits?.includes(searchLower) ||
      acc.category?.name?.toLowerCase().includes(searchLower)
    );
    setFilteredAccounts(filtered);
    setPage(0);
  }, [searchTerm, accounts]);

  // Permission check
  if (!canViewAccounts()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Accounts Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Accounts section. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading && accounts.length === 0) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <AccountBalanceIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 3
            }}>
              Loading Expense Accounts
            </Typography>
            <LinearProgress />
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Card sx={{ 
              position: 'relative',
              borderRadius: 3,
              mt: 5,
              mb: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: theme.palette.grey[100],
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
                opacity: 1
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 3, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}>
                    <AccountBalanceIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main', 
                      mb: 1,
                      fontSize: '2rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Expense Accounts
                    </Typography>
                    <Typography variant="subtitle1" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 700, 
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}>
                  Manage bank accounts and credit cards used for business expenses.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Search Bar */}
          <Card sx={{
            height: 100,
            mb: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 280,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: 40,
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      py: 1.25
                    }
                  }}
                />
                
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {`${filteredAccounts.length} account${filteredAccounts.length !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Accounts Table */}
          <Paper sx={{ 
            borderRadius: 3, 
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
           }}>
            <TableContainer sx={{ overflowX: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Account Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Last 4 Digits
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Bank Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Account Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Category
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3, width: 150 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((account) => (
                    <TableRow 
                      key={account.id}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: 'white',
                        '&:hover': { 
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {account.account_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          •••• {account.last_four_digits}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2">
                          {account.bank_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        {account.account_type ? (
                          <Chip 
                            label={getAccountTypeLabel(account.account_type)} 
                            size="small"
                            color={account.account_type === 'credit' ? 'warning' : 'default'}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        {account.category ? (
                          <Chip 
                            label={account.category.name} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={canEditAccounts() ? "Edit" : "No edit permission"}>
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleOpenDialog(account)}
                                disabled={!canEditAccounts()}
                                sx={{
                                  color: canEditAccounts() ? 'primary.main' : 'action.disabled',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 1,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': canEditAccounts() ? {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                  } : {},
                                  '&.Mui-disabled': {
                                    color: 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDeleteAccounts() ? "Delete" : "No delete permission"}>
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleDeleteClick(account)}
                                disabled={!canDeleteAccounts()}
                                sx={{
                                  color: canDeleteAccounts() ? 'error.main' : 'action.disabled',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 1,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': canDeleteAccounts() ? {
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                                  } : {},
                                  '&.Mui-disabled': {
                                    color: 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredAccounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No accounts yet. Click the + button to add the first account.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 3,
              mb: 4,
              px: 3
            }}>
              <Paper sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden'
              }}>
                <TablePagination
                  component="div"
                  count={filteredAccounts.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Paper>
            </Box>
          </Paper>

          {canEditAccounts() && (
            <Tooltip title="Add Account">
              <Fab
                color="primary"
                onClick={() => handleOpenDialog()}
                sx={{ 
                  position: 'fixed', 
                  bottom: 24, 
                  right: 24, 
                  width: 100, 
                  height: 100
                }}
              >
                <AddIcon sx={{ fontSize: 32 }} />
              </Fab>
            </Tooltip>
          )}

          {/* Add/Edit Dialog */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editMode ? 'Edit Account' : 'Add Account'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Account Name"
                  value={formData.account_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
                  fullWidth
                  required
                  autoFocus
                  placeholder="e.g., Business Checking"
                />
                <TextField
                  label="Last 4 Digits"
                  value={formData.last_four_digits}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setFormData(prev => ({ ...prev, last_four_digits: value }));
                  }}
                  fullWidth
                  required
                  placeholder="1234"
                  inputProps={{ maxLength: 4 }}
                />
                <TextField
                  label="Bank Name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                  fullWidth
                  placeholder="e.g., Chase, Bank of America"
                />
                <FormControl fullWidth>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={formData.account_type}
                    label="Account Type"
                    onChange={(e) => setFormData(prev => ({ ...prev, account_type: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {ACCOUNT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                    Category
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenCategorySelectionDialog}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 1.5,
                      px: 2,
                      height: 56,
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      border: `1px solid rgba(0,0,0,0.23)`,
                      color: formData.category_id ? 'text.primary' : 'text.secondary',
                      fontWeight: 400,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        backgroundColor: '#f8f9fa',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    {formData.category_id ? 
                      (() => {
                        const category = categories.find(c => c.id === formData.category_id);
                        return category ? category.name : 'Select Category';
                      })() :
                      'Select Category'
                    }
                  </Button>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                {submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this account?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Category Selection Dialog */}
          <Dialog
            open={categorySelectionDialogOpen}
            onClose={handleCloseCategorySelectionDialog}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 4,
                height: '40vh',
                width: '600px',
                maxWidth: '600px',
                margin: 'auto',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 2, 
              fontSize: '1.25rem', 
              fontWeight: 500,
              color: 'text.primary',
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                Select Category
              </Typography>
              <IconButton onClick={handleCloseCategorySelectionDialog} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                <TextField
                  fullWidth
                  placeholder="Search categories..."
                  value={categorySearchTerm}
                  onChange={(e) => setCategorySearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    sx: { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none'
                      }
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ 
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredCategoriesForSelection().map((category) => (
                      <TableRow 
                        key={category.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2,
                            pl: 3,
                            pr: 3
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {category.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDialogCategorySelect(category)}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.8rem',
                              px: 2,
                              py: 1,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              bgcolor: dialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                              color: dialogSelectedCategory?.id === category.id ? 'white' : 'inherit',
                              '&:hover': {
                                bgcolor: dialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                                border: `1px solid ${theme.palette.primary.main}`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {getFilteredCategoriesForSelection().length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {categorySearchTerm ? 'No categories found matching your search.' : 'No categories available.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              gap: 2, 
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <Button
                onClick={handleCloseCategorySelectionDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmCategorySelection}
                variant="contained"
                disabled={!dialogSelectedCategory}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Fade>
  );
};

export default AccountsPage;